/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useMemo, useState } from "react";
import { When } from "react-if";
import { Box, Checkbox, Message, Spacer, Table, theme } from "@nordcloud/gnui";
import { DriftResolveStatus } from "~/generated/graphql";
import { BreadcrumbsBox, Pagination, UniversalWrap } from "~/components";
import { useQueryState } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import {
  DriftHeader,
  DriftRow,
  DriftsBanner,
  DriftsLoader,
} from "./components";
import {
  useAllDriftsUpdate,
  useDrifts,
  useDriftsSelect,
  useDriftUpdate,
} from "./hooks";
import { FilteringParams } from "./types";

export function DriftReportPage() {
  const [filteringParams, setFilteringParams] = useState<FilteringParams>();

  const { set } = useQueryState();

  const { allIds, count, drifts, isLoading, error } = useDrifts({
    ...filteringParams,
  });
  const [hasAllSelected, setHasAllSelected] = useState(false);

  const {
    hasAllOnPageSelected,
    hasAnySelected,
    hasNoneSelected,
    showBanner,
    limit,
    page,
    selectedIds,
    onDriftIdToggle,
    onAllDriftIdsToggle,
    isDriftIdSelected,
  } = useDriftsSelect({ count });

  const [updateDrift, { loading: isUpdateDriftLoading }] = useDriftUpdate();
  const [updateAllDrifts, { loading: isUpdateAllDriftsLoading }] =
    useAllDriftsUpdate();

  const breadcrumbsLabels = [
    { label: "Dashboard", uri: ROUTES.index },
    { label: "Allocation Drifts", uri: "" },
  ];

  useEffect(() => {
    if (isUpdateDriftLoading) {
      onAllDriftIdsToggle([]);
    }
  }, [onAllDriftIdsToggle, isUpdateDriftLoading]);

  const params = useMemo(
    () => ({
      page,
      limit,
      filteringParams,
    }),
    [page, limit, filteringParams]
  );

  const handleSelection = async (status: DriftResolveStatus) => {
    await (hasAllSelected
      ? updateAllDrifts(status, params)
      : updateDrift(status, selectedIds, params));

    if (hasAllSelected) {
      set("page")(0);
    }
  };

  const handleHasAllSelectedUpdate = (newValue: boolean) => {
    setHasAllSelected(newValue);

    if (newValue === false) {
      onAllDriftIdsToggle([]);
    }
  };

  const handleDriftStatusChanged = (status: DriftResolveStatus, id: string[]) =>
    updateDrift(status, id, params);

  return (
    <>
      <BreadcrumbsBox
        title="Allocation Drifts"
        customBreadcrumbs={breadcrumbsLabels}
      />
      <Box margin="spacing04">
        <DriftHeader
          hasNoneSelected={hasNoneSelected}
          filteringParams={filteringParams}
          isLoading={
            isLoading || isUpdateDriftLoading || isUpdateAllDriftsLoading
          }
          onHandleSelection={handleSelection}
          onSetFilteringParams={setFilteringParams}
        />
        <Spacer height={theme.spacing.spacing04} />
        <When condition={showBanner}>
          <DriftsBanner
            limit={limit}
            count={count}
            hasAllSelected={hasAllSelected}
            onAllSelected={handleHasAllSelectedUpdate}
          />
          <Spacer height={theme.spacing.spacing04} />
        </When>

        <UniversalWrap
          loaderProps={{ loading: isLoading, Component: DriftsLoader }}
          errorProps={{ error }}
        >
          <When condition={count > 0}>
            <Table css={{ tableLayout: "fixed" }}>
              <Table.thead>
                <Table.tr>
                  <Table.th css={{ width: "1rem", verticalAlign: "middle" }}>
                    <Checkbox
                      withoutLabel
                      isIndeterminate={hasAnySelected}
                      checked={hasAllOnPageSelected}
                      onChange={() => onAllDriftIdsToggle(allIds)}
                    />
                  </Table.th>
                  <Table.th css={{ width: "2rem" }} />
                  <Table.th css={{ width: "20rem" }}>
                    Resource Name / Resource Details
                  </Table.th>
                  <Table.th css={{ textAlign: "right" }}>
                    Current Application / Environment
                  </Table.th>
                  <Table.th css={{ width: "1.5rem" }} />
                  <Table.th>Target Application / Environment</Table.th>
                  <Table.th css={{ width: "10.5rem" }} />
                </Table.tr>
              </Table.thead>
              <Table.tbody>
                {drifts.map((drift) => (
                  <DriftRow
                    key={drift.id}
                    drift={drift}
                    isSelected={isDriftIdSelected(drift.id)}
                    disabled={isUpdateDriftLoading || isUpdateAllDriftsLoading}
                    onDriftIdToggle={onDriftIdToggle}
                    onDriftStatusChanged={handleDriftStatusChanged}
                  />
                ))}
              </Table.tbody>
            </Table>
            <Pagination count={count} />
          </When>
          <When condition={count === 0}>
            <Message status="notification" image="info">
              There are no drifts found
            </Message>
          </When>
        </UniversalWrap>
      </Box>
    </>
  );
}
